import { useEffect, useState } from "react";
import "@/App.css";
import {
  Phone,
  MapPin,
  Clock,
  Instagram,
  ArrowUpRight,
  Flame,
  Soup,
  Sparkles,
  ChefHat,
  Truck,
} from "lucide-react";

const PHONE_DISPLAY = "07404 522889";
const PHONE_TEL = "tel:+447404522889";
const ADDRESS_LINE_1 = "Unit F, Chefzhub, Mount Avenue";
const ADDRESS_LINE_2 = "Bletchley, MK1 1LS";
const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Chefzhub+Mount+Avenue+Bletchley+MK1+1LS";
const UBER_URL =
  "https://www.order.store/store/dum-on-the-run/cK4gSnKcQLmOmJyB8BLP3A";
const INSTAGRAM_URL = "https://www.instagram.com/dumontherun/";
const WHATSAPP_URL = "https://chat.whatsapp.com/CBat4hguxO2GSiD6DqrNQR";
const HOURS_TEXT = "Mon – Sun · 11:30 am – 10:00 pm";
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_dum-delivery/artifacts/t8shh131_4383EF84-67C0-40B2-932B-73554ACFA62C.PNG";

const HERO_DISHES = [
  {
    label: "Chicken Biryani",
    img: "https://customer-assets.emergentagent.com/job_dum-delivery/artifacts/nxo0oiem_chicken%20biryani.jpeg",
    rotate: "-5deg",
  },
  {
    label: "Lamb Biryani",
    img: "https://customer-assets.emergentagent.com/job_dum-delivery/artifacts/4go1yadh_lamb%20biriyani.jpeg",
    rotate: "4deg",
  },
  {
    label: "Chicken 65",
    img: "https://customer-assets.emergentagent.com/job_dum-delivery/artifacts/4ye15tlr_chicken%2065.jpeg",
    rotate: "-3deg",
  },
  {
    label: "Chilli Chicken",
    img: "https://customer-assets.emergentagent.com/job_dum-delivery/artifacts/apy5xq2r_chilli%20chicken.jpeg",
    rotate: "5deg",
  },
  {
    label: "Veg Biryani",
    img: "https://customer-assets.emergentagent.com/job_dum-delivery/artifacts/slm9hh5q_veg%20biryani.jpeg",
    rotate: "-4deg",
  },
  {
    label: "Apricot Delight",
    img: "https://customer-assets.emergentagent.com/job_dum-delivery/artifacts/xjprs6xr_UPdtaed%20Apricot%20delight.webp",
    rotate: "3deg",
  },
  {
    label: "Rose Milk",
    img: "https://customer-assets.emergentagent.com/job_dum-delivery/artifacts/uawv2s3i_Updated%20rose%20milk.jpeg",
    rotate: "-3deg",
  },
];

/* ---------- Live "Open / Closed" status ---------- */
function useOpenStatus() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  // 11:30 (690 min) – 22:00 (1320 min), every day
  const minutes = now.getHours() * 60 + now.getMinutes();
  const isOpen = minutes >= 690 && minutes < 1320;
  return { isOpen, now };
}

/* ---------- Marquee ---------- */
const MarqueeRow = ({ items, reverse = false }) => (
  <div className="marquee py-4 border-y-2 border-black bg-[var(--ink)] text-[var(--bone)] overflow-hidden">
    <div className={`marquee-track ${reverse ? "reverse" : ""}`}>
      {[...Array(2)].map((_, dup) => (
        <div key={dup} className="flex items-center gap-10">
          {items.map((it, i) => (
            <div key={`${dup}-${i}`} className="flex items-center gap-6">
              <span className="font-bebas text-2xl md:text-3xl tracking-widest">
                {it}
              </span>
              <span className="text-[var(--gold)] text-2xl">✦</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

/* ---------- Section divider ---------- */
const Divider = ({ label }) => (
  <div className="flex items-end justify-between gap-4 mb-8 md:mb-12">
    <div className="flex items-center gap-4">
      <span className="h-px w-12 md:w-24 bg-[var(--ink)]/30" />
      <span className="font-bebas text-xl md:text-2xl tracking-widest">
        {label}
      </span>
    </div>
    <span className="font-brush text-3xl md:text-4xl text-[var(--gold-2)] hidden md:inline">
      handi-fired
    </span>
  </div>
);

export default function App() {
  const { isOpen, now } = useOpenStatus();

  const day = now.toLocaleDateString("en-GB", { weekday: "long" });
  const timeStr = now.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <main
      data-testid="dum-home"
      className="min-h-screen relative overflow-x-hidden bg-[var(--bone)] text-[var(--ink)]"
    >
      {/* =========== TOP BAR =========== */}
      <header className="relative z-10 border-b-2 border-[var(--ink)]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-3 flex items-center justify-between gap-4">
          <div
            className="flex items-center gap-3"
            data-testid="brand-mark"
          >
            <img
              src={LOGO_URL}
              alt="Dum On The Run logo"
              className="w-11 h-11 md:w-14 md:h-14 object-contain"
            />
            <div className="leading-none">
              <p className="font-bebas text-xl md:text-2xl tracking-widest">
                DUM ON THE RUN
              </p>
              <p className="font-body text-[10px] md:text-xs tracking-[0.25em] uppercase text-[var(--ink)]/70">
                Milton Keynes · Aylesbury · Hatfield
              </p>
            </div>
          </div>

          <div
            data-testid="open-status"
            className={`sticker px-3 md:px-4 py-1.5 text-[10px] md:text-xs border-2 ${
              isOpen
                ? "bg-[var(--ink)] text-[var(--bone)] border-[var(--ink)]"
                : "bg-[var(--bone)] text-[var(--ink)] border-[var(--ink)]"
            }`}
          >
            <span
              className={`pulse-dot inline-block w-2 h-2 rounded-full mr-2 ${
                isOpen ? "bg-[var(--gold)]" : "bg-[var(--chili)]"
              }`}
              style={{ color: isOpen ? "var(--gold)" : "var(--chili)" }}
            />
            {isOpen ? "OPEN NOW" : "CLOSED"} · {day} {timeStr}
          </div>
        </div>
      </header>

      {/* =========== HERO =========== */}
      <section className="relative grain overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pt-10 md:pt-16 pb-12 md:pb-20">
          <div className="grid md:grid-cols-12 gap-10 md:gap-8 items-center">
            {/* LEFT — text */}
            <div className="md:col-span-7">
              {/* Tagline tiny row */}
              <div className="reveal d1 flex flex-wrap items-center gap-3 mb-6">
                <span className="sticker text-[10px] md:text-xs px-3 py-1 bg-[var(--ink)] text-[var(--bone)]">
                  ★ Slow Cooked · Handi Fired
                </span>
                <span className="sticker text-[10px] md:text-xs px-3 py-1 border-2 border-[var(--ink)] text-[var(--ink)]">
                  Massive Portions
                </span>
                <span className="font-brush text-2xl md:text-3xl text-[var(--ink)]/80">
                  fresh from the handi pot
                </span>
              </div>

              {/* Huge type */}
              <h1 className="reveal d2 font-display leading-[0.82] tracking-tight">
                <span className="block text-[16vw] md:text-[7.5rem] lg:text-[10rem]">
                  DUM.
                </span>
                <span className="block text-[16vw] md:text-[7.5rem] lg:text-[10rem] outline-text">
                  ON THE
                </span>
                <span className="block text-[16vw] md:text-[7.5rem] lg:text-[10rem] relative">
                  RUN<span className="text-[var(--ink)]">.</span>
                  <Sparkles className="absolute -top-3 -right-3 md:-top-4 md:-right-6 w-7 h-7 md:w-12 md:h-12 text-[var(--ink)] wobble" />
                </span>
              </h1>

              {/* Sub copy + CTAs */}
              <div className="reveal d3 mt-8 md:mt-10">
                <p className="text-base md:text-lg font-body leading-relaxed max-w-xl">
                  Biryani HQ for Milton Keynes, Aylesbury & Hatfield —
                  handi-fired out of our Bletchley kitchen. <span className="font-bold">Massive portions
                  nobody can beat — in size <em>or</em> quality.</span> Heavy-bottom
                  pots, dum-sealed slow cooking, ingredients fresh every single
                  day. Order in, collect, or get it dropped to your door.
                </p>

                <div className="mt-7 md:mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href={UBER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="cta-order-ubereats-hero"
                    className="brand-link btn-ink inline-flex items-center justify-center gap-3 px-6 py-4 font-archivo text-sm md:text-base tracking-widest"
                  >
                    ORDER ON UBER EATS
                    <ArrowUpRight className="w-5 h-5" />
                  </a>
                  <a
                    href={PHONE_TEL}
                    data-testid="cta-call-hero"
                    className="brand-link btn-outline inline-flex items-center justify-center gap-3 px-6 py-4 font-archivo text-sm md:text-base tracking-widest"
                  >
                    <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
                  </a>
                </div>
              </div>
            </div>

            {/* RIGHT — dish image cluster */}
            <div
              data-testid="hero-dish-strip"
              className="reveal d4 md:col-span-5"
            >
              <div className="dish-cluster">
                {HERO_DISHES.map((d, i) => (
                  <figure
                    key={i}
                    data-testid={`hero-dish-${i}`}
                    className="dish-card group"
                    style={{ "--r": d.rotate }}
                  >
                    <div className="dish-img-wrap">
                      <img
                        src={d.img}
                        alt={d.label}
                        loading="lazy"
                        className="dish-img"
                      />
                    </div>
                    <figcaption className="dish-label">{d.label}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Decorative brush blobs */}
        <div className="pointer-events-none absolute -left-20 top-32 w-[40vw] h-[40vw] rounded-full brush-gold opacity-30 blur-3xl" />
        <div className="pointer-events-none absolute -right-32 -bottom-20 w-[34vw] h-[34vw] rounded-full bg-[var(--ink)]/10 blur-3xl" />
      </section>

      {/* =========== MARQUEE =========== */}
      <MarqueeRow
        items={[
          "MASSIVE PORTIONS · UNBEATABLE SIZE & QUALITY",
          "FRESH FROM THE HANDI POT",
          "SLOW COOKED · DUM SEALED",
          "MILTON KEYNES · AYLESBURY · HATFIELD",
          "OPEN 7 DAYS",
          "ORDER · COLLECT · DELIVER",
          "BIRYANI HEADQUARTERS — BLETCHLEY",
        ]}
      />

      {/* =========== VALUE / PILLARS =========== */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <Divider label="THE BRAND" />

        <div className="grid md:grid-cols-12 gap-8 items-start">
          <h2 className="md:col-span-7 font-display text-5xl md:text-7xl leading-[0.9] tracking-tight">
            Big flavour.
            <br />
            <span className="text-[var(--gold-2)]">Bigger plates.</span>
            <br />
            Zero shortcuts.
          </h2>

          <p className="md:col-span-5 text-base md:text-lg font-body leading-relaxed text-[var(--ink)]/85">
            Every plate at Dum On The Run starts in a heavy-bottomed handi —
            sealed, slow, and stupidly aromatic. Tender meats, fragrant rice,
            portions that genuinely fill you up. Nobody beats us on size or
            quality. Try it once and you'll see why.
          </p>
        </div>

        {/* Pillars */}
        <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
          {[
            { icon: <Soup className="w-7 h-7" />, label: "Massive portions" },
            { icon: <ChefHat className="w-7 h-7" />, label: "Authentic dum cooking" },
            { icon: <Flame className="w-7 h-7" />, label: "Rich aroma & flavour" },
            { icon: <Sparkles className="w-7 h-7" />, label: "Fresh ingredients daily" },
            { icon: <Truck className="w-7 h-7" />, label: "MK · Aylesbury · Hatfield" },
          ].map((p, i) => (
            <div
              key={i}
              data-testid={`pillar-${i}`}
              className="lift border-2 border-[var(--ink)] bg-[var(--bone-2)] p-5 md:p-6"
            >
              <div className="w-12 h-12 border-2 border-[var(--ink)] flex items-center justify-center bg-[var(--bone)] mb-4">
                {p.icon}
              </div>
              <p className="font-bebas tracking-widest text-lg md:text-xl">
                {p.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* =========== ORDER BLOCK =========== */}
      <section className="relative">
        <div className="bg-[var(--ink)] text-[var(--bone)] relative overflow-hidden">
          <div className="absolute inset-0 stripe-bg opacity-[0.06] pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <p className="font-bebas tracking-[0.35em] text-[var(--gold)] text-sm md:text-base mb-4">
                  GET IT DELIVERED
                </p>
                <h2 className="font-display text-5xl md:text-7xl leading-[0.9]">
                  Tap. Order.
                  <br />
                  <span className="text-[var(--gold-soft)]">Feast.</span>
                </h2>
                <p className="mt-6 max-w-xl text-base md:text-lg text-[var(--bone)]/80">
                  Hot, sealed, and on its way. Order directly through Uber Eats
                  — we cook, they ride.
                </p>
              </div>

              <div className="md:col-span-4 flex md:justify-end">
                <a
                  href={UBER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cta-order-ubereats-main"
                  className="brand-link group inline-flex items-center gap-4 px-8 py-6 bg-white text-[var(--ink)] border-2 border-white hover:bg-transparent hover:text-white transition-all"
                >
                  <Truck className="w-7 h-7" />
                  <span className="font-archivo text-base md:text-lg tracking-widest">
                    UBER EATS
                  </span>
                  <ArrowUpRight className="w-6 h-6 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========== INFO GRID (contact / hours / location) =========== */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <Divider label="THE DETAILS" />

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {/* PHONE */}
          <a
            href={PHONE_TEL}
            data-testid="info-phone"
            className="brand-link lift border-2 border-[var(--ink)] bg-[var(--bone-2)] p-6 md:p-8 flex flex-col gap-6 group"
          >
            <div className="flex items-center justify-between">
              <span className="font-archivo text-xs tracking-[0.3em] text-[var(--ink)]/70">
                CALL
              </span>
              <Phone className="w-6 h-6" />
            </div>
            <div>
              <p className="font-bebas text-sm tracking-widest text-[var(--ink)]/70 mb-2">
                Ring the kitchen
              </p>
              <p className="font-display text-3xl md:text-4xl leading-tight tick">
                {PHONE_DISPLAY}
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm font-archivo tracking-widest group-hover:text-[var(--gold-2)] transition-colors">
              TAP TO CALL <ArrowUpRight className="w-4 h-4" />
            </div>
          </a>

          {/* HOURS */}
          <div
            data-testid="info-hours"
            className="lift border-2 border-[var(--ink)] bg-[var(--ink)] text-[var(--bone)] p-6 md:p-8 flex flex-col gap-6"
          >
            <div className="flex items-center justify-between">
              <span className="font-archivo text-xs tracking-[0.3em] text-[var(--gold)]">
                HOURS
              </span>
              <Clock className="w-6 h-6 text-[var(--gold)]" />
            </div>
            <div>
              <p className="font-bebas text-sm tracking-widest text-[var(--bone)]/70 mb-2">
                Open every day
              </p>
              <p className="font-display text-3xl md:text-4xl leading-tight">
                11:30 AM
                <span className="text-[var(--gold)]"> → </span>
                10:00 PM
              </p>
              <p className="font-body text-sm mt-3 text-[var(--bone)]/75">
                {HOURS_TEXT}
              </p>
            </div>
            <div
              className={`flex items-center gap-3 text-xs font-archivo tracking-[0.25em] ${
                isOpen ? "text-[var(--gold-soft)]" : "text-[var(--chili)]"
              }`}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{
                  background: isOpen ? "var(--gold)" : "var(--chili)",
                }}
              />
              {isOpen ? "FIRING RIGHT NOW" : "BACK TOMORROW"}
            </div>
          </div>

          {/* LOCATION */}
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="info-location"
            className="brand-link lift border-2 border-[var(--ink)] bg-[var(--bone-2)] text-[var(--ink)] p-6 md:p-8 flex flex-col gap-6 group"
          >
            <div className="flex items-center justify-between">
              <span className="font-archivo text-xs tracking-[0.3em]">
                FIND US
              </span>
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <p className="font-bebas text-sm tracking-widest mb-2">
                HQ · Milton Keynes · Aylesbury · Hatfield
              </p>
              <p className="font-display text-2xl md:text-3xl leading-tight">
                {ADDRESS_LINE_1}
              </p>
              <p className="font-display text-2xl md:text-3xl leading-tight">
                {ADDRESS_LINE_2}
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm font-archivo tracking-widest group-hover:translate-x-1 transition-transform">
              OPEN IN MAPS <ArrowUpRight className="w-4 h-4" />
            </div>
          </a>
        </div>
      </section>

      {/* =========== BIG CTA STRIP =========== */}
      <section className="relative">
        <div className="bg-[var(--bone-2)] border-y-2 border-[var(--ink)]">
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-12 md:py-16 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <p className="font-brush text-3xl md:text-5xl text-[var(--gold-2)]">
                Hungry yet?
              </p>
              <h3 className="font-display text-4xl md:text-6xl leading-[0.95] mt-1">
                Don't sleep on the biryani.
              </h3>
            </div>
            <div className="md:col-span-5 flex flex-col sm:flex-row gap-3 md:justify-end">
              <a
                href={UBER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-order-ubereats-strip"
                className="brand-link btn-ink inline-flex items-center justify-center gap-3 px-6 py-4 font-archivo tracking-widest"
              >
                ORDER NOW <ArrowUpRight className="w-5 h-5" />
              </a>
              <a
                href={PHONE_TEL}
                data-testid="cta-call-strip"
                className="brand-link btn-outline inline-flex items-center justify-center gap-3 px-6 py-4 font-archivo tracking-widest"
              >
                <Phone className="w-5 h-5" /> CALL US
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* =========== SOCIAL =========== */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <Divider label="MEMBERS CLUB" />

        <div className="grid md:grid-cols-5 gap-5 md:gap-6">
          {/* WhatsApp — featured */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="social-whatsapp"
            className="brand-link lift border-2 border-[var(--ink)] bg-[var(--ink)] text-[var(--bone)] p-6 md:p-10 md:col-span-3 flex flex-col md:flex-row md:items-center justify-between gap-6 md:gap-8 group relative overflow-hidden"
          >
            <div className="absolute top-4 right-4 sticker px-2.5 py-1 text-[10px] tracking-[0.2em] bg-[var(--bone)] text-[var(--ink)] border-2 border-[var(--bone)]">
              EXCLUSIVE
            </div>

            <div className="flex items-center gap-5">
              <div className="w-16 h-16 md:w-20 md:h-20 border-2 border-[var(--bone)] flex items-center justify-center bg-[var(--bone)] text-[var(--ink)] shrink-0">
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-8 h-8 md:w-10 md:h-10"
                  aria-hidden="true"
                >
                  <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.16 1.6 5.97L0 24l6.18-1.62A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.19-1.24-6.19-3.48-8.52ZM12 22a9.94 9.94 0 0 1-5.07-1.39l-.36-.22-3.67.96.98-3.58-.24-.37A9.94 9.94 0 1 1 22 12c0 5.52-4.48 10-10 10Zm5.47-7.5c-.3-.15-1.78-.88-2.05-.98-.27-.1-.47-.15-.66.15-.2.3-.76.97-.93 1.17-.17.2-.35.22-.65.08-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.78-1.67-2.08-.18-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.38-.03-.53-.08-.15-.66-1.6-.9-2.2-.24-.58-.49-.5-.66-.5-.17 0-.37-.02-.57-.02-.2 0-.52.07-.8.37-.27.3-1.05 1.03-1.05 2.5s1.07 2.9 1.22 3.1c.15.2 2.1 3.22 5.1 4.52.71.3 1.27.48 1.71.6.72.23 1.37.2 1.88.12.57-.09 1.78-.73 2.03-1.43.25-.7.25-1.3.17-1.43-.07-.13-.27-.2-.57-.35Z" />
                </svg>
              </div>
              <div>
                <p className="font-bebas text-xs md:text-sm tracking-[0.3em] text-[var(--bone)]/70 mb-1">
                  WHATSAPP GROUP
                </p>
                <p className="font-display text-3xl md:text-5xl leading-[0.95]">
                  Members-only prices.
                </p>
                <p className="font-body text-sm md:text-base text-[var(--bone)]/80 mt-3 max-w-md">
                  Join our WhatsApp group for exclusive prices, secret menu
                  drops & subscriber-only deals you won't find anywhere else.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-archivo text-sm tracking-widest shrink-0 group-hover:translate-x-1 transition-transform">
              JOIN <ArrowUpRight className="w-5 h-5" />
            </div>
          </a>

          {/* Instagram */}
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="social-instagram"
            className="brand-link lift border-2 border-[var(--ink)] bg-[var(--bone-2)] p-6 md:p-8 md:col-span-2 flex items-center justify-between gap-6 group"
          >
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 md:w-16 md:h-16 border-2 border-[var(--ink)] flex items-center justify-center bg-[var(--ink)] text-[var(--gold)]">
                <Instagram className="w-7 h-7" />
              </div>
              <div>
                <p className="font-bebas text-sm tracking-[0.25em] text-[var(--ink)]/70">
                  INSTAGRAM
                </p>
                <p className="font-display text-2xl md:text-3xl leading-tight">
                  @dumontherun
                </p>
                <p className="font-body text-xs text-[var(--ink)]/70 mt-2">
                  Daily food drops & behind-the-handi.
                </p>
              </div>
            </div>
            <ArrowUpRight className="w-7 h-7 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </div>
      </section>

      {/* =========== MARQUEE 2 =========== */}
      <MarqueeRow
        reverse
        items={[
          "★ DUM ON THE RUN ★",
          "JOIN WHATSAPP — MEMBERS-ONLY PRICES",
          "MILTON KEYNES · AYLESBURY · HATFIELD",
          "HQ: BLETCHLEY MK1 1LS",
          "07404 522889",
          "OPEN DAILY 11:30 – 22:00",
          "★ BIRYANI HQ ★",
          "ORDER ON UBER EATS",
        ]}
      />

      {/* =========== FOOTER =========== */}
      <footer className="relative bg-[var(--bone)] border-t-2 border-[var(--ink)]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-12 md:py-16 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-6">
            <img
              src={LOGO_URL}
              alt="Dum On The Run"
              className="w-40 md:w-56 h-auto mb-5"
            />
            <p className="font-brush text-2xl md:text-3xl text-[var(--ink)]/70">
              fresh from the handi pot.
            </p>
          </div>

          <div className="md:col-span-3 space-y-2">
            <p className="font-archivo text-xs tracking-[0.3em] text-[var(--ink)]/60 mb-3">
              HQ & DELIVERY
            </p>
            <p className="font-body text-sm leading-relaxed">
              {ADDRESS_LINE_1}
              <br />
              {ADDRESS_LINE_2}
            </p>
            <p className="font-body text-xs leading-relaxed text-[var(--ink)]/70 pt-1">
              Serving Milton Keynes, Aylesbury & Hatfield.
            </p>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-maps"
              className="brand-link inline-flex items-center gap-1 text-sm font-archivo tracking-widest mt-2 hover:text-[var(--gold-2)] transition-colors"
            >
              GET DIRECTIONS <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>

          <div className="md:col-span-3 space-y-2">
            <p className="font-archivo text-xs tracking-[0.3em] text-[var(--ink)]/60 mb-3">
              CONTACT
            </p>
            <a
              href={PHONE_TEL}
              data-testid="footer-phone"
              className="brand-link block font-display text-2xl hover:text-[var(--gold-2)] transition-colors"
            >
              {PHONE_DISPLAY}
            </a>
            <p className="font-body text-sm text-[var(--ink)]/75">
              {HOURS_TEXT}
            </p>
            <div className="flex gap-3 mt-3">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                aria-label="Instagram"
                className="brand-link w-9 h-9 border-2 border-[var(--ink)] flex items-center justify-center hover:bg-[var(--ink)] hover:text-[var(--bone)] transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                aria-label="WhatsApp"
                className="brand-link w-9 h-9 border-2 border-[var(--ink)] flex items-center justify-center hover:bg-[var(--ink)] hover:text-[var(--gold)] transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                  <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.16 1.6 5.97L0 24l6.18-1.62A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.19-1.24-6.19-3.48-8.52Z" />
                </svg>
              </a>
              <a
                href={UBER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-uber"
                aria-label="Uber Eats"
                className="brand-link h-9 px-3 border-2 border-[var(--ink)] flex items-center justify-center hover:bg-[var(--gold)] transition-colors font-archivo text-[10px] tracking-[0.2em]"
              >
                UBER EATS
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-[var(--ink)]/20">
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 text-xs font-archivo tracking-[0.2em] text-[var(--ink)]/70">
            <span>© {new Date().getFullYear()} DUM ON THE RUN · ALL RIGHTS RESERVED</span>
            <span>MADE WITH HEAVY POTS · BLETCHLEY HQ · SERVING MK · AYLESBURY · HATFIELD</span>
          </div>
        </div>
      </footer>
    </main>
  );
}
